import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isEqual } from "lodash";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";

import { Dialog, DialogContent, Divider, IconButton } from "@mui/material";

import DialogTitle from "../DialogTitle/DialogTitle";
import DialogActions from "../DialogActions/DialogActions";
import ButtonWithBadge from "./ButtonWithBadge/ButtonWithBadge";
import ConfirmationChangesModal from "../ConfirmationChangesDialog/ConfirmationChangesDialog";
import Document from "../Document/Document/Document";

import { handleDownloadDocsFiles } from "../../services/documents/document-files-api.service";

import { DocumentDTO } from "../../models/documents/document-dto";

import { DocumentEvents } from "../Document/Document/utils";

import useAuthorization from "../../custom-hooks/useAuthorization/useAuthorization";

import { AUTHORIZATIONS } from "../../utils/constants/authorization-constants";

import { Props, State, DocumentReadOnlyField } from "./interfaces";

import { styled } from "@mui/material/styles";
import { Typography } from "@mui/material";

const { DownloadDocuments } = AUTHORIZATIONS.LegalEntityAccess;
const { CompanyAccountsConfig } = AUTHORIZATIONS.SystemAccess;

export const AsLink = styled(Typography)(({ theme }) => ({
  cursor: "pointer",
  borderBottom: `1px solid ${theme.palette.primary.main}`,
  transition: "all .3s ease",

  "&:hover": {
    borderBottom: `1px solid ${theme.palette.primary.contrastText}`,
  },
}));

const AttachedDocuments = ({
  count,
  documentIDs,
  transaction,
  period,
  hasAllVerifiedDocuments,
  hasAllProcessedDocuments,
  handleNotifyParent,
  handleNotifyParentUponDelete,
  buttonSize,
  isWidget,
  showAsLink = false,
  linkText,
  documentsReadOnlyFields,
  hideReadOnlyFields,
  useSimpleView,
  maxFilesNumber,
  handleReturnDocumentToParent,
  simpleViewAlert,
  documentsNames,
  deleteDocumentIfEmpty = false,
  closeAfterDocumentDelete = false,
  linkStyle,
  CustomButtonContent,
  documentIsReadOnly,
  badgeVerticalPosition,
  badgeHorizontalPosition,
  customDocAndFilesNames,
  checkIfFileDeletable,
  allowShare,
  isGenericDocument,
  allowDownloadContent,
  onlyAcceptedExtensions,
}: Props) => {
  const { t } = useTranslation();

  const allowDownload = useAuthorization([DownloadDocuments]);
  const isSysAdmin = useAuthorization([CompanyAccountsConfig]);

  const [state, setState] = useState<State>({
    documentIsMutated: false,
    stateChanged: false,
    documentIDs,
    isDialogOpen: false,
    isConfirmationChangesDialogOpen: false,
    expandedDocumentID: "",
    deletedDocumentId: "",
    filesNumber: count,
  });

  useEffect(() => {
    if (state.deletedDocumentId) {
      if (documentIDs.find((id) => id === state.deletedDocumentId)) {
        setState((state) => ({
          ...state,
          documentIDs: documentIDs.filter((id) => id !== state.deletedDocumentId),
        }));
      }
    } else {
      setState((state) => ({
        ...state,
        documentIDs: documentIDs,
        deletedDocumentId: "",
      }));
    }
  }, [documentIDs]);

  const setDialogOpen = (dialog: "isDialogOpen" | "isConfirmationChangesDialogOpen", value: boolean) =>
    setState((state) => ({
      ...state,
      [dialog]: value,
      expandedDocumentID: dialog === "isDialogOpen" ? "" : state.expandedDocumentID,
    }));

  const handleClose = () => {
    state.documentIsMutated
      ? setDialogOpen("isConfirmationChangesDialogOpen", true)
      : setDialogOpen("isDialogOpen", false);

    if (!isEqual(state.documentIDs, documentIDs) && handleNotifyParent) {
      handleNotifyParent();
    }
  };

  const handleDiscard = () =>
    setState((state) => ({
      ...state,
      documentIsMutated: false,
      isDialogOpen: false,
      isConfirmationChangesDialogOpen: false,
    }));

  const handleNotify = (event: DocumentEvents, document?: DocumentDTO | null) => {
    switch (event) {
      case DocumentEvents.DOCUMENT_IS_MUTATED:
        setState((state) => ({
          ...state,
          documentIsMutated: true,
        }));
        break;

      case DocumentEvents.DOCUMENT_IS_DELETED:
        setState((state) => ({
          ...state,
          documentIDs: state.documentIDs.filter((id) => document?.id !== id),
          deletedDocumentId: document?.id ?? "",
          stateChanged: true,
        }));

        closeAfterDocumentDelete && setDialogOpen("isDialogOpen", false);

        handleNotifyParent && handleNotifyParent();
        handleNotifyParentUponDelete && handleNotifyParentUponDelete(document?.id ?? "");
        break;

      case DocumentEvents.DOCUMENT_IS_SAVED:
        setState((state) => ({
          ...state,
          documentIsMutated: false,
          stateChanged: true,
          filesNumber: document?.files.length ?? 0,
        }));
        // setDialogOpen("isDialogOpen", false);
        handleNotifyParent && handleNotifyParent();

        document && handleReturnDocumentToParent && handleReturnDocumentToParent(document);

        break;

      case DocumentEvents.DOCUMENT_IS_NOT_AUTHORIZED:
        setDialogOpen("isDialogOpen", false);
        break;
    }
  };

  const handleControlledExpand = (id: string) => {
    setState((state) => ({
      ...state,
      expandedDocumentID: id,
    }));
  };

  return (
    <>
      {!showAsLink ? (
        !!CustomButtonContent ? (
          <IconButton onClick={() => setDialogOpen("isDialogOpen", true)}>{CustomButtonContent}</IconButton>
        ) : (
          <ButtonWithBadge
            verified={hasAllVerifiedDocuments}
            processed={hasAllProcessedDocuments}
            handleClick={() => setDialogOpen("isDialogOpen", true)}
            count={count}
            size={buttonSize}
            documentsNames={documentsNames}
          />
        )
      ) : (
        <AsLink
          style={{ ...linkStyle }}
          onClick={() => setDialogOpen("isDialogOpen", true)}
        >
          <span>{linkText}</span>
        </AsLink>
      )}
      <Dialog
        fullWidth
        open={state.isDialogOpen}
        onClose={handleClose}
      >
        <DialogTitle
          title={t(
            `components.attached_documents.${
              state.documentIDs?.length ? (state.documentIDs.length > 1 ? "editing_more" : "editing_one") : "adding_one"
            }`
          )}
          handleClose={handleClose}
          additionalButtons={
            state.documentIDs.length > 1 &&
            (allowDownload || isSysAdmin) && (
              <IconButton onClick={() => handleDownloadDocsFiles(state.documentIDs, customDocAndFilesNames)}>
                <FontAwesomeIcon icon={faDownload} />
              </IconButton>
            )
          }
        />
        <DialogContent>
          {state.documentIDs?.length ? (
            state.documentIDs.map((id) => (
              <React.Fragment key={id}>
                <Document
                  key={id}
                  documentID={id}
                  transaction={transaction}
                  handleNotify={handleNotify}
                  isWidget={isWidget}
                  readOnlyFields={
                    documentsReadOnlyFields
                      ? documentsReadOnlyFields?.filter(
                          (document: DocumentReadOnlyField) => document.documentID === id
                        )[0]?.readOnlyFields
                      : undefined
                  }
                  hideReadOnlyFields={hideReadOnlyFields}
                  handleControlledExpand={handleControlledExpand}
                  expanded={state.expandedDocumentID === id}
                  maxFilesNumber={maxFilesNumber}
                  useSimpleView={useSimpleView}
                  documentIsReadOnly={documentIsReadOnly}
                  simpleViewAlert={simpleViewAlert}
                  deleteDocumentIfEmpty={deleteDocumentIfEmpty}
                  forceClose={handleNotifyParent}
                  customDocAndFilesNames={customDocAndFilesNames}
                  checkIfFileDeletable={checkIfFileDeletable}
                  allowShare={allowShare}
                  isGenericDocument={isGenericDocument}
                  allowDownloadContent={allowDownloadContent}
                  onlyAcceptedExtensions={onlyAcceptedExtensions}
                />
                <Divider />
              </React.Fragment>
            ))
          ) : (
            <Document
              transaction={transaction}
              documentFieldsProps={{ period }}
              handleNotify={handleNotify}
              isWidget={isWidget}
              customDocAndFilesNames={customDocAndFilesNames}
              checkIfFileDeletable={checkIfFileDeletable}
              isGenericDocument={isGenericDocument}
              onlyAcceptedExtensions={onlyAcceptedExtensions}
            />
          )}
        </DialogContent>
        <DialogActions handleCancel={handleClose} />
        <ConfirmationChangesModal
          open={state.isConfirmationChangesDialogOpen}
          handleCancel={() => setDialogOpen("isConfirmationChangesDialogOpen", false)}
          handleDiscard={handleDiscard}
        />
      </Dialog>
    </>
  );
};

export default AttachedDocuments;
